/**
 * 小组件引导弹窗核心逻辑
 * 封装环境判断、版本判断、疲劳度控制、全局每日限制、Bridge 调用
 *
 * 使用示例:
 * await tryShowWidgetGuide({ pageType: 'mallList', source: 'mall_list' });
 */
import { Native } from '@hb/mbridge';
import { compareVersions } from 'compare-versions';
import dayjs from 'dayjs';
import { isHelloFamilyApp, isAndroid } from '../env/env';
import wisdom from './wisdomConfig';
import { getStorageItem, setStorageItem } from './storage';
import nativeMgr from '../native/native';
import logger from './logger';

// 小组件模板 ID
const WIDGET_TEMPLATE_ID = 'travel_card';

// 最低 App 版本要求
const MIN_APP_VERSION = '6.99.45';

// localStorage key 前缀
const STORAGE_PREFIX = 'widget_guide';

// 全局每日限制 key
const GLOBAL_DAILY_KEY = `${STORAGE_PREFIX}_global_daily`;

// 页面类型
type WidgetPageType = 'mallList' | 'payResult' | 'orderDetail';

// 疲劳度配置
interface WidgetFatigueConfig {
  mallList: number;
  payResult: number;
  orderDetail: number;
}

// 默认疲劳度天数（wisdom 获取失败时的兜底值）
const DEFAULT_FATIGUE_DAYS = 3;

// 默认疲劳度配置
const DEFAULT_FATIGUE: WidgetFatigueConfig = {
  mallList: DEFAULT_FATIGUE_DAYS,
  payResult: DEFAULT_FATIGUE_DAYS,
  orderDetail: DEFAULT_FATIGUE_DAYS,
};

// 允许展示小组件引导的业务线 categoryId 白名单
const WIDGET_ALLOWED_CATEGORIES = [
  '0', // 全部
  '101', // 单车
  '103', // 助力车
  '1238', // 换电
];

// categoryId → 英文标识映射（用于拼接 source）
const CATEGORY_SOURCE_MAP: Record<string, string> = {
  0: 'all',
  101: 'bike',
  103: 'ebike',
  1238: 'charge',
};

// pageType → source 前缀映射
const PAGE_SOURCE_PREFIX: Record<WidgetPageType, string> = {
  mallList: 'mall_list',
  payResult: 'pay_result',
  orderDetail: 'order_detail',
};

/**
 * 根据页面类型和业务线 categoryId 生成 source
 * 格式：页面前缀_业务线英文名，如 mall_list_bike
 * categoryId 不在映射中时 fallback 到原始 source
 */
const buildWidgetSource = (
  pageType: WidgetPageType,
  categoryId?: string,
  fallbackSource?: string,
): string => {
  const prefix = PAGE_SOURCE_PREFIX[pageType];
  if (categoryId && CATEGORY_SOURCE_MAP[categoryId]) {
    return `${prefix}_${CATEGORY_SOURCE_MAP[categoryId]}`;
  }
  return fallbackSource || prefix;
};

// tryShowWidgetGuide 参数
interface ShowWidgetGuideOptions {
  /** 页面类型：mallList / payResult / orderDetail */
  pageType: WidgetPageType;
  /** 业务来源标识，用于客户端埋点 */
  source: string;
  /** 当前业务线 categoryId，用于白名单校验，不传则跳过校验 */
  categoryId?: string;
}

/**
 * 获取设备厂商（仅 Android 可用）
 * 通过 Bridge hellobike/marketing/ads.getBrand 获取
 * @returns 厂商名称（如 "Xiaomi"、"HUAWEI"），失败返回空字符串
 */
const getDeviceBrand = (): Promise<string> => new Promise((resolve) => {
  if (!isAndroid) {
    resolve('');
    return;
  }
  Native.callNative({
    classMap: 'hellobike/marketing/ads',
    method: 'getBrand',
    params: {},
  }).then((response: any) => {
    const brand = response?.brand || response?.data?.brand || '';
    logger('[小组件] 获取设备厂商:', brand, response);
    resolve(brand);
  }).catch((err) => {
    logger('[小组件] 获取设备厂商失败:', err);
    resolve('');
  });
});

// 小米品牌标识（不区分大小写匹配）
const XIAOMI_BRANDS = ['xiaomi', 'redmi'];

/**
 * 检查是否为小米系手机
 */
const isXiaomiDevice = (brand: string): boolean => {
  const lowerBrand = brand.toLowerCase();
  return XIAOMI_BRANDS.some((b) => lowerBrand.includes(b));
};

/**
 * 检查当前 App 版本是否满足指定最低版本要求
 *
 * 通过 nativeMgr.getDeviceInfo() 获取当前 App 版本号，
 * 使用 compare-versions 库进行语义化版本比较。
 *
 * 兜底策略：
 * - minVersion 未传入时，使用模块常量 MIN_APP_VERSION（6.99.45）
 * - getDeviceInfo 调用失败时，返回 false（视为版本不满足）
 * - 版本号格式异常时，compareVersions 会抛错，catch 后返回 false
 *
 * @param minVersion - 最低版本号，如 '6.99.45'、'7.0.0'，不传则使用默认值
 * @returns true 表示当前 App 版本 >= minVersion
 *
 * 使用示例:
 * await checkVersionCompatibility();         // 默认判断 >= 6.99.45
 * await checkVersionCompatibility('7.0.0');  // 判断 >= 7.0.0
 */
export const checkVersionCompatibility = async (minVersion?: string): Promise<boolean> => {
  const target = minVersion || MIN_APP_VERSION;
  try {
    const { version } = await nativeMgr.getDeviceInfo();
    if (!version) return false;
    return compareVersions(version, target) >= 0;
  } catch {
    return false;
  }
};

/**
 * 获取疲劳度配置（从 wisdom 远程获取，失败使用默认值）
 */
const getFatigueConfig = async (): Promise<WidgetFatigueConfig> => {
  try {
    const config = await wisdom.get('widget_fatigue', DEFAULT_FATIGUE);
    return config || DEFAULT_FATIGUE;
  } catch {
    return DEFAULT_FATIGUE;
  }
};

/**
 * 检查全局每日一次限制
 * @returns true 表示今天已弹过，不可再弹
 */
const isGlobalDailyLimitReached = (): boolean => {
  // storage 自带过期判断（当天结束过期），有值即表示今天已弹过
  const val = getStorageItem(GLOBAL_DAILY_KEY);
  logger('[小组件] 检查全局每日一次限制 检查 有值即表示今天已弹过', { key: GLOBAL_DAILY_KEY, val });
  return val !== null;
};

/**
 * 检查当前页面疲劳度是否在冷却期内
 * @returns true 表示冷却期内，不可弹出
 */
const isPageFatigueActive = (pageType: WidgetPageType): boolean => {
  const key = `${STORAGE_PREFIX}_${pageType}`;
  // storage 自带过期判断，未过期返回值，已过期返回 null
  const val = getStorageItem(key);
  logger('[小组件] 检查当前页面疲劳度是否在冷却期内 true 表示冷却期内，不可弹出', { key, val });
  return val !== null;
};

/**
 * 记录弹窗展示（更新页面疲劳度 + 全局每日计数）
 */
const recordWidgetShow = (pageType: WidgetPageType, fatigueDays: number): void => {
  const now = dayjs().format('YYYY-MM-DD HH:mm:ss');

  // 记录当前页面疲劳度
  const pageKey = `${STORAGE_PREFIX}_${pageType}`;
  setStorageItem({
    key: pageKey,
    val: now,
    expireIn: fatigueDays,
  });

  // 记录全局每日计数（当天结束过期）
  const todayEnd = dayjs().endOf('day').toDate();
  setStorageItem({
    key: GLOBAL_DAILY_KEY,
    val: now,
    expireIn: todayEnd,
  });

  logger('[小组件] recordWidgetShow 已写入 storage', { pageKey, fatigueDays, now });
};

/**
 * 查询小组件是否已添加到桌面
 * @returns true 表示已添加
 */
const queryWidgetAdded = (): Promise<boolean> => new Promise((resolve) => {
  Native.callNative({
    classMap: 'hellobike/platform/widget',
    method: 'queryWidgetStatus',
    params: { templateId: WIDGET_TEMPLATE_ID },
  }).then((response: any) => {
    logger('[小组件] 查询小组件是否添加 返回isAdded 为true 或者1表示添加', response);
    resolve(!!response?.isAdded);
  }).catch((err) => {
    logger('[小组件] 查询小组件是否添加 调用失败', err);
    // Bridge 调用失败，视为未添加，继续尝试弹窗
    resolve(false);
  });
});

/**
 * 调用客户端展示小组件引导弹窗
 * @returns true 表示弹窗指令已下发（Bridge 未 reject）
 */
const showWidgetGuidePopup = (source: string): Promise<boolean> => new Promise((resolve) => {
  Native.callNative({
    classMap: 'hellobike/platform/widget',
    method: 'showAddWidgetGuideView',
    params: {
      templateId: WIDGET_TEMPLATE_ID,
      source,
    },
  }).then((response: any) => {
    logger('[小组件]展示小组件引导弹窗  返回response?.code==0可弹出', response);
    // 只要 Bridge 正常返回（未 reject）就视为弹窗已下发，记录疲劳度
    // 客户端弹窗展示由客户端控制，H5 无法精确判断是否真的展示
    resolve(true);
  }).catch((err: any) => {
    logger('[小组件] 展示小组件引导弹窗 调用失败', err);
    resolve(false);
  });
});

// ============================================================
// 交互冲突管理：处理小组件弹窗与商品详情弹窗/收银台的冲突
// ============================================================

/**
 * 交互冲突状态
 * - idle: 无冲突
 * - detail: 商品详情弹窗打开中（暂停小组件弹窗，等关闭后再弹）
 * - cashier: 收银台打开中（直接取消本次小组件弹窗机会）
 */
type InteractionType = 'detail' | 'cashier';

// 当前活跃的交互类型
let activeInteraction: InteractionType | null = null;

// 待执行的小组件弹窗（详情弹窗关闭后执行）
let pendingGuideCallback: (() => void) | null = null;

/**
 * 标记商品详情弹窗打开
 * 如果小组件计时器到期时详情弹窗正在展示，弹窗会被暂停等待关闭后执行
 */
export function onDetailPopupOpen(): void {
  activeInteraction = 'detail';
  logger('[小组件] 其他弹窗打开，暂停小组件');
}

/**
 * 标记商品详情弹窗关闭
 * 如果有待执行的小组件弹窗，立即执行
 */
export function onDetailPopupClose(): void {
  if (activeInteraction === 'detail') {
    activeInteraction = null;
    logger('[小组件] 其他弹窗关闭');
  }
  // 如果有待弹的小组件，执行它
  if (pendingGuideCallback && activeInteraction === null) {
    logger('[小组件] 执行暂停中的小组件弹窗');
    const cb = pendingGuideCallback;
    pendingGuideCallback = null;
    cb();
  }
}

/**
 * 标记收银台触发（购买下单）
 * 直接取消本次小组件弹窗机会
 */
export function onCashierTrigger(): void {
  activeInteraction = 'cashier';
  pendingGuideCallback = null;
  logger('[小组件] 收银台触发，取消小组件弹窗机会');
}

/**
 * 重置交互冲突状态（页面失活/卸载时调用，避免跨页面状态污染）
 */
export function resetInteractionState(): void {
  activeInteraction = null;
  pendingGuideCallback = null;
  logger('[小组件] 重置交互冲突状态');
}

/**
 * 尝试展示小组件引导弹窗（完整判断链路）
 *
 * 判断顺序：交互冲突 → 环境 → 版本 → 全局每日限制 → 页面疲劳度 → 查询添加状态 → 弹窗
 *
 * @param options.pageType - 页面类型，决定使用哪个疲劳度配置
 * @param options.source - 业务来源标识，传给客户端用于埋点
 * @returns true 表示成功展示了弹窗
 */
export async function tryShowWidgetGuide(options: ShowWidgetGuideOptions): Promise<boolean> {
  const { pageType, source, categoryId } = options;
  // 根据业务线维度拼接最终 source
  const finalSource = buildWidgetSource(pageType, categoryId, source);

  try {
    logger('[小组件] 展示小组件引导弹窗（完整判断链路）开始执行', { pageType, source: finalSource, categoryId });

    // 0. 交互冲突检查
    if (activeInteraction === 'cashier') {
      logger('[小组件] 收银台已触发，取消本次弹窗');
      return false;
    }
    if (activeInteraction === 'detail') {
      // 详情弹窗打开中，暂存弹窗请求，等关闭后执行
      logger('[小组件] 有其他弹窗打开中，暂停小组件弹窗等待其他弹窗关闭');
      return new Promise<boolean>((resolve) => {
        pendingGuideCallback = () => {
          tryShowWidgetGuide(options).then(resolve);
        };
      });
    }

    // 1. 环境判断：仅哈啰 App
    if (!isHelloFamilyApp) {
      logger('[小组件] 非哈啰 App 环境，跳过');
      return false;
    }

    // 2. 业务线白名单校验
    if (options.categoryId && !WIDGET_ALLOWED_CATEGORIES.includes(options.categoryId)) {
      logger('[小组件] 当前业务线不在白名单内，跳过', { categoryId: options.categoryId });
      return false;
    }

    // 3. 版本判断：>= 6.99.45
    const versionOk = await checkVersionCompatibility();
    if (!versionOk) {
      logger('[小组件] App 版本不满足要求，跳过');
      return false;
    }

    // 3.5 小米手机不弹小组件（Android 通过 getBrand Bridge 判断）
    if (isAndroid) {
      const brand = await getDeviceBrand();
      if (isXiaomiDevice(brand)) {
        logger('[小组件] 小米手机，跳过弹窗', { brand });
        return false;
      }
    }

    // 4. 全局每日一次检查
    if (isGlobalDailyLimitReached()) {
      logger('[小组件] 全局每日限制已达，跳过');
      return false;
    }

    // 4. 当前页面疲劳度检查
    if (isPageFatigueActive(pageType)) {
      logger('[小组件] 页面疲劳度冷却中，跳过', pageType);
      return false;
    }

    // 5. 获取疲劳度配置
    const fatigueConfig = await getFatigueConfig();
    const fatigueDays = fatigueConfig[pageType] || DEFAULT_FATIGUE_DAYS;
    logger('[小组件] 疲劳度配置', { fatigueDays, fatigueConfig });

    // 6. 查询小组件状态
    const isAdded = await queryWidgetAdded();
    if (isAdded) {
      logger('[小组件] 小组件已添加，跳过');
      return false;
    }

    // 7. 展示引导弹窗
    logger('[小组件] 条件全部满足，展示弹窗');
    const shown = await showWidgetGuidePopup(finalSource);

    // 8. 弹窗成功后记录疲劳度
    if (shown) {
      recordWidgetShow(pageType, fatigueDays);
      logger('[小组件] 弹窗展示成功，已记录疲劳度');
    } else {
      logger('[小组件] 弹窗展示失败');
    }

    return shown;
  } catch (err) {
    logger('[widgetGuide] tryShowWidgetGuide 异常', err);
    return false;
  }
}
